import {
  ADMIN_PIN,
  ADMIN_PIN_SESSION_KEY,
  SITE_STATE_WRITE_TOKEN_CLIENT,
  SYNC_CLEARED_EVENT,
  SYNC_ERROR_EVENT,
} from "@/constants/admin";

const SYNC_MS = 700;

/** KV/R2 calls: `pin` + optional Bearer from `VITE_SITE_STATE_WRITE_TOKEN` (after PIN login only). */
export function hasAdminSession(): boolean {
  try {
    if (!ADMIN_PIN) return false;
    return sessionStorage.getItem(ADMIN_PIN_SESSION_KEY) === ADMIN_PIN;
  } catch {
    return false;
  }
}

function buildWritePayload(base: Record<string, unknown>): Record<string, unknown> {
  return ADMIN_PIN ? { ...base, pin: ADMIN_PIN } : { ...base };
}

/** Authorization: Bearer from build env, only while the admin PIN session is active. */
export function writeAuthHeaders(): Headers {
  const h = new Headers();
  h.set("Content-Type", "application/json");
  if (hasAdminSession() && SITE_STATE_WRITE_TOKEN_CLIENT) {
    h.set("Authorization", `Bearer ${SITE_STATE_WRITE_TOKEN_CLIENT}`);
  }
  return h;
}

export function writeAuthHeadersForMultipart(): Headers {
  const h = new Headers();
  if (hasAdminSession() && SITE_STATE_WRITE_TOKEN_CLIENT) {
    h.set("Authorization", `Bearer ${SITE_STATE_WRITE_TOKEN_CLIENT}`);
  }
  return h;
}

async function reportPutResult(path: string, res: Response): Promise<void> {
  if (res.ok) {
    window.dispatchEvent(new Event(SYNC_CLEARED_EVENT));
    return;
  }
  const raw = await res.text();
  let message = `${path} failed (${res.status})`;
  try {
    const j = JSON.parse(raw) as { error?: string };
    if (j.error) message = j.error;
  } catch {
    if (raw) message = raw.slice(0, 280);
  }
  window.dispatchEvent(new CustomEvent(SYNC_ERROR_EVENT, { detail: { message } }));
}

export function scheduleCloudPut(
  timerRef: { current: ReturnType<typeof setTimeout> | undefined },
  path: string,
  body: Record<string, unknown>,
): void {
  clearTimeout(timerRef.current);
  timerRef.current = setTimeout(() => {
    void fetch(path, {
      method: "PUT",
      headers: writeAuthHeaders(),
      body: JSON.stringify(buildWritePayload(body)),
    })
      .then((res) => reportPutResult(path, res))
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        window.dispatchEvent(
          new CustomEvent(SYNC_ERROR_EVENT, {
            detail: { message: `${path}: ${msg} (network or blocked request)` },
          }),
        );
      });
  }, SYNC_MS);
}

/** Push current catalog + availability to KV immediately (admin “Publish now”). */
export async function publishSiteStateNow(
  catalog: {
    edits: Record<string, unknown>;
    removed: string[];
    purged: string[];
    added: unknown[];
  },
  availability: Record<string, boolean>,
): Promise<{ ok: boolean; error?: string }> {
  const [r1, r2] = await Promise.all([
    fetch("/api/catalog", {
      method: "PUT",
      headers: writeAuthHeaders(),
      body: JSON.stringify(
        buildWritePayload({
          edits: catalog.edits,
          removed: catalog.removed,
          purged: catalog.purged,
          added: catalog.added,
        }),
      ),
    }),
    fetch("/api/availability", {
      method: "PUT",
      headers: writeAuthHeaders(),
      body: JSON.stringify(buildWritePayload({ overrides: availability })),
    }),
  ]);

  const errors: string[] = [];
  if (!r1.ok) {
    const j = (await r1.json().catch(() => ({}))) as { error?: string };
    errors.push(j.error ?? `catalog ${r1.status}`);
  }
  if (!r2.ok) {
    const j = (await r2.json().catch(() => ({}))) as { error?: string };
    errors.push(j.error ?? `availability ${r2.status}`);
  }
  if (errors.length) return { ok: false, error: errors.join("; ") };
  window.dispatchEvent(new Event(SYNC_CLEARED_EVENT));
  return { ok: true };
}
